import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import GbCensusOverlay from "./overlays/gbCensusOverlay";
import { Coordinate } from "ol/coordinate";
import GbCensusVectorTileLayer from "./layers/GbCensusVectroTileLayer";
import GbCensusSelectedBoundaryLayer from "./layers/GbCensusSelectedBoundaryLayer";
import { useLayerService } from "../../../../hooks";
import { CensusMapLocation, GbCensusSelectLocation } from "./interractions";
import { useParams } from "react-router-dom";
import { MapView } from "../../../../constant/VectorTileLayerType";
import {
  useGetCensusParams,
  CensusViewTools,
  useGetCensusCategories,
} from "./controls";
import GbCensusMessages from "./messages/GbCensusMessages";
import { useViewContext } from "../../../../context/View/ViewContext";

const ENGLAND_SCOTLAND_EXTENT: [number, number, number, number] = [
  -950000, 6437484, 203683, 8650000,
];

const GbCensusMapView = () => {
  const { outputAreaId, year, categoryId, columnId } = useGetCensusParams();
  const { categories, loading: categoriesLoading } = useGetCensusCategories();
  const layerService = useLayerService();
  const { view, setLoading } = useViewContext();

  const [selectedLocation, setSelectedLocation] =
    useState<CensusMapLocation | null>(null);

  const [coordinate, setCoordinate] = useState<Coordinate | undefined>(
    undefined,
  );

  const [selectedColumnValue, setSelectedColumnValue] = useState<number | null>(
    null,
  );

  const visible = useParams().layer === MapView.GB_CENSUS;

  const [showValidationError, setShowValidationError] = useState(false);

  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMapClick = useCallback(
    (location: CensusMapLocation, coordinate: Coordinate) => {
      setSelectedLocation(location);

      setTimeout(() => {
        if (categoryId == null || columnId == null) {
          setShowValidationError(true);
          setCoordinate(coordinate);
          view.animate({ center: coordinate });
          return;
        }

        setShowValidationError(false);
        view.animate({ center: coordinate });

        loadingTimeoutRef.current = setTimeout(() => {
          setLoading(true);
        }, 1000);

        layerService
          .GetColumnValueForOutputArea(year!, columnId!, location.outputAreaId)
          .then((x) => {
            setSelectedColumnValue(x);
            setCoordinate(coordinate);
          })
          .finally(() => {
            if (loadingTimeoutRef.current) {
              clearTimeout(loadingTimeoutRef.current);
              loadingTimeoutRef.current = null;
            }
            setLoading(false);
          });
      });
    },
    [
      categoryId,
      columnId,
      year,
      view,
      setSelectedLocation,
      setCoordinate,
      setLoading,
    ],
  );

  const closeOverlay = useCallback(() => {
    setSelectedLocation(null);
    setCoordinate(undefined);
    setShowValidationError(false);
  }, [setSelectedLocation, setCoordinate]);

  useEffect(() => {
    if (visible) {
      view.fit(ENGLAND_SCOTLAND_EXTENT, {
        duration: 500,
        padding: [100, 100, 100, 100],
      });
    }
  }, [visible, view]);

  useEffect(() => {
    closeOverlay();
    setShowValidationError(false);
  }, [closeOverlay, outputAreaId, year, categoryId]);

  useEffect(() => {
    if (selectedLocation && columnId != null) {
      loadingTimeoutRef.current = setTimeout(() => {
        setLoading(true);
      }, 1000);

      layerService
        .GetColumnValueForOutputArea(
          year!,
          columnId!,
          selectedLocation.outputAreaId,
        )
        .then((x) => {
          setSelectedColumnValue(x);
          setShowValidationError(false);
        })
        .finally(() => {
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
            loadingTimeoutRef.current = null;
          }
          setLoading(false);
        });
    }
  }, [columnId]);

  const selectedCategoryTitle = useMemo(
    () =>
      categories
        .flatMap((c) => c.columns)
        .find((col) => col.columnId === columnId)?.name,
    [categories, columnId],
  );

  return (
    <>
      {" "}
      <GbCensusVectorTileLayer visible={visible} />
      <GbCensusSelectLocation
        onSelectLocation={handleMapClick}
        active={visible && outputAreaId != null}
      />
      <GbCensusSelectedBoundaryLayer selectedLocation={selectedLocation} />
      <GbCensusOverlay
        closeOverlay={closeOverlay}
        coordinate={coordinate}
        value={selectedColumnValue}
        categoryTitle={selectedCategoryTitle}
        message={
          showValidationError ?
            categoryId != null ?
              "Please select a column."
            : "Please select a category & column."
          : undefined
        }
      />
      {visible && (
        <>
          <CensusViewTools
            categories={categories}
            categoriesLoading={categoriesLoading}
            showValidationError={showValidationError}
          />
          <GbCensusMessages />
        </>
      )}
    </>
  );
};

export default GbCensusMapView;
